/**
 * User Form Page
 *
 * Form for creating and editing users
 * Supports both create and edit modes based on route params
 * Admin-only access
 *
 * @module pages/users/UserForm
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Spinner,
  Alert,
  InputGroup,
} from 'react-bootstrap';
import { toast } from '../../utils/toast';
import {
  createUser,
  updateUser,
  getUserById,
} from '../../services/userService';
import { useAuth } from '../../contexts/AuthContext';
import DashboardLayout from '../../components/layout/DashboardLayout';

/**
 * Password validation regex
 * At least 8 characters, 1 uppercase, 1 number
 */
const passwordRegex = /^(?=.*[A-Z])(?=.*\d).{8,}$/;

/**
 * Form validation schema for create mode
 */
const createUserSchema = yup.object().shape({
  email: yup
    .string()
    .required('El email es requerido')
    .email('Email inválido')
    .max(255, 'El email no puede exceder 255 caracteres'),
  password: yup
    .string()
    .required('La contraseña es requerida')
    .min(8, 'La contraseña debe tener al menos 8 caracteres')
    .matches(
      passwordRegex,
      'La contraseña debe contener al menos 1 mayúscula y 1 número'
    ),
  full_name: yup
    .string()
    .required('El nombre es requerido')
    .min(2, 'El nombre debe tener al menos 2 caracteres')
    .max(255, 'El nombre no puede exceder 255 caracteres'),
  role: yup
    .string()
    .required('El rol es requerido')
    .oneOf(['admin', 'supervisor', 'technician', 'operario', 'mensajero', 'client'], 'Rol inválido'),
  company_name: yup
    .string()
    .max(255, 'El nombre de la compañía no puede exceder 255 caracteres'),
  phone: yup
    .string()
    .max(20, 'El teléfono no puede exceder 20 caracteres'),
});

/**
 * Form validation schema for edit mode
 *
 * NO lleva `email` a propósito. `profiles` **no tiene columna `email`**
 * (verificado contra prod: `information_schema.columns` devuelve 0 filas para
 * `table_name='profiles' AND column_name='email'`), así que:
 *
 *   - `getUserById` no puede devolverlo → el campo se pintaba siempre vacío;
 *   - el `.required()` que estaba acá bloqueaba el submit con "El email es
 *     requerido" sobre un campo que nadie podía llenar con el valor real;
 *   - y aunque se tipeara, `updateUser` lo descarta con `ALLOWED_PROFILE_FIELDS`
 *     (`userService.js`), o sea que el admin creía haber cambiado el correo de
 *     login y no cambiaba nada.
 *
 * El correo vive en `auth.users`, que sólo se toca por la Admin API (el mismo
 * carril que usa `create_user`). Mientras no exista esa ruta de edición, la
 * pantalla no ofrece el campo: ofrecer una acción condenada a no guardar es
 * peor que no ofrecerla.
 */
const editUserSchema = yup.object().shape({
  full_name: yup
    .string()
    .required('El nombre es requerido')
    .min(2, 'El nombre debe tener al menos 2 caracteres')
    .max(255, 'El nombre no puede exceder 255 caracteres'),
  role: yup
    .string()
    .required('El rol es requerido')
    .oneOf(['admin', 'supervisor', 'technician', 'operario', 'mensajero', 'client'], 'Rol inválido'),
  company_name: yup
    .string()
    .max(255, 'El nombre de la compañía no puede exceder 255 caracteres'),
  phone: yup
    .string()
    .max(20, 'El teléfono no puede exceder 20 caracteres'),
});

/**
 * UserForm component
 */
const UserForm = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id: userId } = useParams();
  const { isAdmin } = useAuth();
  const isEditMode = !!userId;

  const [loading, setLoading] = useState(false);
  const [fetchingUser, setFetchingUser] = useState(isEditMode);
  const [error, setError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);

  // React Hook Form
  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    watch,
  } = useForm({
    resolver: yupResolver(isEditMode ? editUserSchema : createUserSchema),
    defaultValues: {
      email: '',
      password: '',
      full_name: '',
      role: 'client',
      company_name: '',
      phone: '',
    },
  });

  const selectedRole = watch('role');

  /**
   * Check permissions
   */
  useEffect(() => {
    if (!isAdmin) {
      toast.error('No tienes permisos para crear o editar usuarios');
      navigate('/dashboard');
    }
  }, [isAdmin, navigate]);

  /**
   * Fetch user data if editing
   */
  useEffect(() => {
    if (!isEditMode) return;

    const fetchUserData = async () => {
      try {
        setFetchingUser(true);
        const { data, error } = await getUserById(userId);

        if (error) throw error;

        // Sin `email`: la columna no existe en `profiles` (ver editUserSchema).
        // Poner `email: data.email || ''` acá era lo que dejaba el campo en
        // blanco y hacía saltar el `required` contra un valor imposible.
        reset({
          full_name: data.full_name || '',
          role: data.role || 'client',
          company_name: data.company_name || '',
          phone: data.phone || '',
        });
      } catch (error) {
        console.error('Fetch user error:', error);
        setError(error.message);
        toast.error('Error al cargar el usuario');
      } finally {
        setFetchingUser(false);
      }
    };

    fetchUserData();
  }, [userId, isEditMode, reset]);

  /**
   * Handle form submission
   */
  const onSubmit = async (formData) => {
    try {
      setLoading(true);
      setError(null);

      // Clean up empty values
      const cleanData = {
        ...formData,
        company_name: formData.company_name || null,
        phone: formData.phone || null,
      };

      let result;
      if (isEditMode) {
        // Fuera `password` y `email`: ninguno de los dos vive en `profiles`.
        // `updateUser` ya los filtraría con ALLOWED_PROFILE_FIELDS, pero
        // mandarlos igual hace que `validateUserData` valide un email que la
        // pantalla ni siquiera pide.
        const { password, email, ...updateData } = cleanData;
        result = await updateUser(userId, updateData);
      } else {
        result = await createUser(cleanData);
      }

      if (result.error) throw result.error;

      toast.success(
        isEditMode
          ? 'Usuario actualizado exitosamente'
          : 'Usuario creado exitosamente'
      );

      queryClient.invalidateQueries(['users']);
      queryClient.invalidateQueries(['employees']);

      // Navigate back to user list
      navigate('/usuarios');
    } catch (error) {
      console.error('Submit error:', error);
      setError(error.message);
      toast.error(
        isEditMode
          ? 'Error al actualizar el usuario'
          : 'Error al crear el usuario'
      );
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle cancel
   */
  const handleCancel = () => {
    navigate('/usuarios');
  };

  /**
   * Toggle password visibility
   */
  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  // Loading state while fetching user
  if (fetchingUser) {
    return (
      <DashboardLayout>
        <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Cargando usuario...</span>
          </Spinner>
        </Container>
      </DashboardLayout>
    );
  }

  // Error state
  if (error && isEditMode) {
    return (
      <DashboardLayout>
        <Container className="mt-4">
          <Alert variant="danger">
            <Alert.Heading>Error al cargar el usuario</Alert.Heading>
            <p>{error}</p>
            <Button variant="outline-danger" onClick={() => navigate('/usuarios')}>
              Volver a Usuarios
            </Button>
          </Alert>
        </Container>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <Container fluid className="py-4">
      <Row className="justify-content-center">
        <Col lg={8}>
          <Card>
            <Card.Header className="bg-primary text-white">
              <h4 className="mb-0">
                <i className={`bi ${isEditMode ? 'bi-pencil-square' : 'bi-person-plus'} me-2`}></i>
                {isEditMode ? 'Editar Usuario' : 'Nuevo Usuario'}
              </h4>
            </Card.Header>
            <Card.Body>
              {error && (
                <Alert variant="danger" dismissible onClose={() => setError(null)}>
                  {error}
                </Alert>
              )}

              <Form onSubmit={handleSubmit(onSubmit)}>
                {/* Email — SÓLO al crear.
                    Al crear va a `auth.users` por la Admin API (`create_user`).
                    Al editar no se muestra: `profiles` no tiene esa columna, así
                    que no hay valor que precargar ni destino donde guardarlo. */}
                {!isEditMode && (
                  <Form.Group className="mb-3" controlId="email">
                    <Form.Label>
                      Email <span className="text-danger">*</span>
                    </Form.Label>
                    <Controller
                      name="email"
                      control={control}
                      render={({ field }) => (
                        <Form.Control
                          {...field}
                          type="email"
                          placeholder="usuario@example.com"
                          isInvalid={!!errors.email}
                          disabled={isSubmitting}
                          data-testid="user-form-email-input"
                        />
                      )}
                    />
                    {errors.email && (
                      <Form.Control.Feedback type="invalid">
                        {errors.email.message}
                      </Form.Control.Feedback>
                    )}
                  </Form.Group>
                )}

                {/* Password - Only for new users */}
                {!isEditMode && (
                  <Form.Group className="mb-3" controlId="password">
                    <Form.Label>
                      Contraseña <span className="text-danger">*</span>
                    </Form.Label>
                    <Controller
                      name="password"
                      control={control}
                      render={({ field }) => (
                        <InputGroup>
                          <Form.Control
                            {...field}
                            type={showPassword ? 'text' : 'password'}
                            placeholder="Min 8 caracteres, 1 mayúscula, 1 número"
                            isInvalid={!!errors.password}
                            disabled={isSubmitting}
                            data-testid="user-form-password-input"
                          />
                          <Button
                            variant="outline-secondary"
                            onClick={togglePasswordVisibility}
                            disabled={isSubmitting}
                          >
                            <i className={`bi ${showPassword ? 'bi-eye-slash' : 'bi-eye'}`}></i>
                          </Button>
                          {errors.password && (
                            <Form.Control.Feedback type="invalid">
                              {errors.password.message}
                            </Form.Control.Feedback>
                          )}
                        </InputGroup>
                      )}
                    />
                    <Form.Text className="text-muted">
                      Mínimo 8 caracteres, debe incluir al menos 1 mayúscula y 1 número.
                    </Form.Text>
                  </Form.Group>
                )}

                {/* Full Name */}
                <Form.Group className="mb-3" controlId="full_name">
                  <Form.Label>
                    Nombre Completo <span className="text-danger">*</span>
                  </Form.Label>
                  <Controller
                    name="full_name"
                    control={control}
                    render={({ field }) => (
                      <Form.Control
                        {...field}
                        type="text"
                        placeholder="Ej: Juan Pérez González"
                        isInvalid={!!errors.full_name}
                        disabled={isSubmitting}
                        data-testid="user-form-fullname-input"
                      />
                    )}
                  />
                  {errors.full_name && (
                    <Form.Control.Feedback type="invalid">
                      {errors.full_name.message}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>

                {/* Role */}
                <Form.Group className="mb-3" controlId="role">
                  <Form.Label>
                    Rol
                  </Form.Label>
                  <Controller
                    name="role"
                    control={control}
                    render={({ field }) => (
                      <Form.Select
                        {...field}
                        isInvalid={!!errors.role}
                        disabled={isSubmitting}
                        data-testid="user-form-role-select"
                      >
                        <option value="client">Cliente</option>
                        <option value="operario">Operario</option>
                        <option value="mensajero">Mensajero</option>
                        <option value="technician">Técnico / Líder</option>
                        <option value="supervisor">Supervisor</option>
                        <option value="admin">Administrador</option>
                      </Form.Select>
                    )}
                  />
                  {errors.role && (
                    <Form.Control.Feedback type="invalid">
                      {errors.role.message}
                    </Form.Control.Feedback>
                  )}
                  <Form.Text className="text-muted">
                    {selectedRole === 'admin' && 'Acceso completo al sistema, puede gestionar usuarios y configuraciones.'}
                    {selectedRole === 'supervisor' && 'Asigna lideres a cotizaciones y aprueba solicitudes de materiales y herramientas. No gestiona usuarios.'}
                    {selectedRole === 'technician' && 'Calcula materiales y horas hombre en cotizaciones (rol Líder), ejecuta trabajo en campo, solicita herramientas al inventario.'}
                    {selectedRole === 'operario' && 'Ve sus tareas y el proyecto asignado, sube avance y fotos, solicita materiales. Sin acceso a precios ni presupuestos.'}
                    {selectedRole === 'mensajero' && 'Traslada herramientas y materiales, ve precio de compra para verificar contra factura. Sin acceso a proyectos ni al presupuesto.'}
                    {selectedRole === 'client' && 'Solo puede ver proyectos asignados y realizar consultas.'}
                  </Form.Text>
                </Form.Group>

                {/* Company Name */}
                <Form.Group className="mb-3" controlId="company_name">
                  <Form.Label>Nombre de Compañía</Form.Label>
                  <Controller
                    name="company_name"
                    control={control}
                    render={({ field }) => (
                      <Form.Control
                        {...field}
                        type="text"
                        placeholder="Opcional - Ej: Hospital San Juan de Dios"
                        isInvalid={!!errors.company_name}
                        disabled={isSubmitting}
                      />
                    )}
                  />
                  {errors.company_name && (
                    <Form.Control.Feedback type="invalid">
                      {errors.company_name.message}
                    </Form.Control.Feedback>
                  )}
                  <Form.Text className="text-muted">
                    Opcional. Especialmente útil para clientes.
                  </Form.Text>
                </Form.Group>

                {/* Phone */}
                <Form.Group className="mb-4" controlId="phone">
                  <Form.Label>Teléfono</Form.Label>
                  <Controller
                    name="phone"
                    control={control}
                    render={({ field }) => (
                      <Form.Control
                        {...field}
                        type="tel"
                        placeholder="Ej: +506 8888-8888"
                        isInvalid={!!errors.phone}
                        disabled={isSubmitting}
                      />
                    )}
                  />
                  {errors.phone && (
                    <Form.Control.Feedback type="invalid">
                      {errors.phone.message}
                    </Form.Control.Feedback>
                  )}
                  <Form.Text className="text-muted">
                    Opcional. Formato recomendado: +506 XXXX-XXXX
                  </Form.Text>
                </Form.Group>

                {/* Action Buttons */}
                <div className="d-flex justify-content-end gap-2">
                  <Button
                    variant="secondary"
                    onClick={handleCancel}
                    disabled={isSubmitting}
                    data-testid="user-form-cancel-button"
                  >
                    Cancelar
                  </Button>
                  <Button
                    variant="primary"
                    type="submit"
                    disabled={isSubmitting || loading}
                    data-testid="user-form-submit-button"
                  >
                    {isSubmitting || loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        {isEditMode ? 'Actualizando...' : 'Creando...'}
                      </>
                    ) : (
                      <>
                        <i className={`bi ${isEditMode ? 'bi-check-lg' : 'bi-plus-lg'} me-2`}></i>
                        {isEditMode ? 'Actualizar Usuario' : 'Crear Usuario'}
                      </>
                    )}
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>

          {/* Help Text */}
          {isEditMode && (
            <Alert variant="info" className="mt-3" data-testid="user-form-edit-note">
              <i className="bi bi-info-circle me-2"></i>
              <strong>Nota:</strong> Esta pantalla edita el perfil (nombre, rol, teléfono, compañía).
              El <strong>correo de acceso</strong> y la <strong>contraseña</strong> no se cambian
              desde acá: para la contraseña usa "Restablecer contraseña" en el menú ⋮ de la lista de usuarios;
              para el correo hay que cambiarlo en la cuenta de acceso, no en el perfil.
            </Alert>
          )}

          {!isEditMode && (
            <Alert variant="info" className="mt-3">
              <i className="bi bi-info-circle me-2"></i>
              {/* Decía "El usuario recibirá un correo de bienvenida con sus
                  credenciales de acceso" y no se manda ningún correo:
                  `createUser` (src/services/userService.js) llama a la tool
                  `create_user` con `email_confirm: true` y nada más. El admin
                  se quedaba esperando un mail que no existe mientras el usuario
                  se quedaba sin saber su contraseña. */}
              <strong>Nota:</strong> No se envía ningún correo. Pasále vos el correo y la
              contraseña que acabas de escribir — con eso ya puede entrar, sin confirmar nada.
              Decile que la cambie desde su perfil apenas entre.
            </Alert>
          )}
        </Col>
      </Row>
      </Container>
    </DashboardLayout>
  );
};

export default UserForm;
